import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Plus, Send, Eye, Loader2, Target, X, CheckCircle2, Clock, AlertCircle, Zap, AlertTriangle, Cloud, MousePointerClick, RefreshCw, CheckSquare, Square, Inbox, Ban } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const STATUS_META = {
  new:     { label: "New",     color: "bg-amber-100 text-amber-800",   icon: Clock },
  sent:    { label: "Sent",    color: "bg-blue-100 text-blue-800",     icon: Send },
  replied: { label: "Replied", color: "bg-purple-100 text-purple-800", icon: Mail },
  booked:  { label: "Booked",  color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  closed:  { label: "Closed",  color: "bg-slate-100 text-slate-500",   icon: X },
};

// Brevo delivery events — what actually happened after send
const DELIVERY_META = {
  accepted:      { label: "Queued",     color: "bg-slate-100 text-slate-600",     icon: Clock,         hint: "Accepted by Brevo. Awaiting delivery confirmation — click Refresh delivery to update." },
  delivered:     { label: "Delivered",  color: "bg-emerald-100 text-emerald-800", icon: Inbox,         hint: "Successfully delivered to the recipient's inbox provider." },
  soft_bounces:  { label: "Soft bounce", color: "bg-amber-100 text-amber-800",    icon: AlertCircle,   hint: "Temporarily rejected (full inbox, auto-reply, server slow). Will retry." },
  hard_bounces:  { label: "Hard bounce", color: "bg-rose-100 text-rose-800",      icon: Ban,           hint: "Permanently rejected — bad address or domain. Do not resend to the same address." },
  blocked:       { label: "Blocked",    color: "bg-rose-100 text-rose-800",       icon: Ban,           hint: "Blocked by the recipient's mail server or Brevo's protection list." },
  invalid_email: { label: "Invalid",    color: "bg-rose-100 text-rose-800",       icon: Ban,           hint: "Address is syntactically invalid or unroutable." },
  spam:          { label: "Spam",       color: "bg-rose-100 text-rose-800",       icon: AlertTriangle, hint: "Recipient marked it as spam." },
  complaints:    { label: "Complaint",  color: "bg-rose-100 text-rose-800",       icon: AlertTriangle, hint: "Recipient filed a complaint with their provider." },
  unsubscribed:  { label: "Unsubscribed", color: "bg-slate-200 text-slate-700",   icon: X,             hint: "Recipient unsubscribed." },
  error:         { label: "Error",      color: "bg-rose-100 text-rose-800",       icon: AlertCircle,   hint: "Brevo reported an error after send." },
};

const inputCls = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500";

const EMPTY = { company_name: "", website: "", hq: "", specialism: "", employee_count: "",
  target_roles: "", email_to: "", contact_first_name: "", phone: "", ai_angle: "",
  email_subject: "", email_body: "" };

export default function AdminProspects() {
  useSeo({ title: "Admin — Enterprise Prospects", noindex: true });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);          // id of expanded prospect
  const [editing, setEditing] = useState(null);    // prospect object being edited (new or existing)
  const [preview, setPreview] = useState(null);    // {subject, html, to}
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false); // pre-flight confirmation modal
  const [bulkTypedConfirm, setBulkTypedConfirm] = useState(""); // user must type SEND
  const [sendConfirm, setSendConfirm] = useState(null); // {prospect} for per-row confirm
  const [job, setJob] = useState(null); // live job progress
  const [flashId, setFlashId] = useState(null); // row id to flash green
  const jobPollRef = useRef(null);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [syncLiveUrl, setSyncLiveUrl] = useState("");
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // {pushed, skipped, failed, errors}
  const [selected, setSelected] = useState(new Set()); // prospect ids ticked for bulk resend
  const [resendConfirm, setResendConfirm] = useState(false);
  const [resendTyped, setResendTyped] = useState("");
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [deliveryResult, setDeliveryResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/prospects");
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.company_name?.trim() || !editing.email_to?.trim()) {
      toast.error("Company name and email are required"); return;
    }
    setBusy(true);
    try {
      if (editing.id) {
        const { id, status, sent_at, created_at, last_error, ...body } = editing;
        await api.put(`/admin/prospects/${id}`, body);
        toast.success("Saved");
      } else {
        await api.post("/admin/prospects", editing);
        toast.success("Prospect added");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this prospect? This cannot be undone.")) return;
    try { await api.delete(`/admin/prospects/${id}`); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doPreview = async (id) => {
    try {
      const { data } = await api.get(`/admin/prospects/${id}/preview`);
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendOne = async (p) => {
    setSendConfirm(null);
    setSendingId(p.id);
    const loadingToast = toast.loading(`Sending to ${p.company_name}…`);
    try {
      await api.post(`/admin/prospects/${p.id}/send`);
      toast.dismiss(loadingToast);
      toast.success(`✓ Email delivered to ${p.email_to}`, {
        description: `${p.company_name} — status updated to Sent at ${new Date().toLocaleTimeString()}`,
        duration: 6000,
      });
      setFlashId(p.id);
      setTimeout(() => setFlashId(null), 2500);
      await load();
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error(`Failed to send to ${p.company_name}`, { description: apiError(e), duration: 10000 });
    }
    setSendingId(null);
  };

  const pollJob = async () => {
    try {
      const { data } = await api.get("/admin/prospects/bulk-job/latest");
      if (!data.job) return;
      setJob(data.job);
      if (data.job.status === "done") {
        clearInterval(jobPollRef.current);
        jobPollRef.current = null;
        setBulkBusy(false);
        const { sent, failed, total } = data.job;
        if (failed === 0) {
          toast.success(`✓ Bulk send complete — ${sent} of ${total} delivered`, { duration: 8000 });
        } else {
          toast.error(`Bulk send finished with ${failed} failure${failed === 1 ? "" : "s"} — ${sent} of ${total} delivered`, { duration: 12000 });
        }
        await load();
      }
    } catch (e) { /* silent — polling is best-effort */ }
  };

  const sendAll = async () => {
    setBulkConfirm(false);
    setBulkTypedConfirm("");
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/prospects/send-all", { only_new: true });
      toast.success(`Starting bulk send — ${data.queued} prospects queued`, {
        description: "Live progress is shown at the top of the page. Emails fire ~2 seconds apart to respect Brevo's rate limits.",
        duration: 6000,
      });
      // Begin polling every 2s
      await pollJob();
      jobPollRef.current = setInterval(pollJob, 2000);
    } catch (e) {
      toast.error(apiError(e), { duration: 10000 });
      setBulkBusy(false);
    }
  };

  // On mount, resume polling if a job is still running, and check sync availability
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/prospects/bulk-job/latest");
        if (data.job && data.job.status === "running") {
          setJob(data.job);
          setBulkBusy(true);
          jobPollRef.current = setInterval(pollJob, 2000);
        }
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/admin/sync-to-live/status");
        setSyncEnabled(!!data.enabled);
        setSyncLiveUrl(data.live_url || "");
      } catch { /* ignore */ }
    })();
    return () => { if (jobPollRef.current) clearInterval(jobPollRef.current); };
  }, []);

  const syncToLive = async () => {
    if (!window.confirm(`Push every prospect in this preview DB that isn't yet in Live (${syncLiveUrl})?\n\nDuplicates are skipped automatically by email address.`)) return;
    setSyncBusy(true);
    setSyncResult(null);
    const t = toast.loading("Syncing prospects to Live…");
    try {
      const { data } = await api.post("/admin/sync-to-live/run");
      toast.dismiss(t);
      setSyncResult(data);
      if (data.failed === 0) {
        toast.success(`✓ Live sync complete — ${data.pushed} pushed, ${data.skipped} already present`, { duration: 8000 });
      } else {
        toast.error(`Live sync finished with ${data.failed} failure${data.failed === 1 ? "" : "s"} — ${data.pushed} pushed, ${data.skipped} already present`, { duration: 12000 });
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(apiError(e), { duration: 10000 });
    }
    setSyncBusy(false);
  };

  const toggleOne = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAllVisible = () => {
    setSelected(prev => {
      const n = new Set(prev);
      items.forEach(p => n.add(p.id));
      return n;
    });
  };
  const selectByStatus = (status) => {
    const ids = items.filter(p => p.status === status).map(p => p.id);
    setSelected(prev => { const n = new Set(prev); ids.forEach(i => n.add(i)); return n; });
  };
  const selectByDelivery = (status) => {
    const ids = items.filter(p => (p.delivery_status || "") === status).map(p => p.id);
    setSelected(prev => { const n = new Set(prev); ids.forEach(i => n.add(i)); return n; });
  };
  const clearSelection = () => setSelected(new Set());

  const resendSelected = async () => {
    setResendConfirm(false);
    setResendTyped("");
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/admin/prospects/bulk-send", { ids });
      toast.success(`Resending to ${data.queued} selected prospect${data.queued === 1 ? "" : "s"}` + (data.skipped ? ` (${data.skipped} skipped — missing subject/body)` : ""), {
        description: "Live progress is shown at the top. Emails fire ~2 seconds apart.",
        duration: 6000,
      });
      clearSelection();
      await pollJob();
      jobPollRef.current = setInterval(pollJob, 2000);
    } catch (e) {
      toast.error(apiError(e), { duration: 10000 });
      setBulkBusy(false);
    }
  };

  const refreshDelivery = async () => {
    setDeliveryBusy(true);
    setDeliveryResult(null);
    const t = toast.loading("Querying Brevo for delivery events (last 14 days)…");
    try {
      const { data } = await api.post("/admin/prospects/refresh-delivery");
      toast.dismiss(t);
      setDeliveryResult(data);
      toast.success(`✓ Delivery status refreshed — ${data.updated} prospect${data.updated === 1 ? "" : "s"} updated`, { duration: 8000 });
      await load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(apiError(e), { duration: 10000 });
    }
    setDeliveryBusy(false);
  };

  const statusCounts = items.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Target size={22} /> Enterprise Prospects</h1>
          <p className="text-sm text-slate-500 mt-1">Cold-outreach pipeline. Each prospect has its own tailored subject and body. Email is sent via Brevo with the Teams &amp; Enterprise header banner, your signature and a CTA to /enterprise.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="prospects-add-btn" onClick={() => setEditing({ ...EMPTY })}
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-slate-800">
            <Plus size={15} /> Add prospect
          </button>
          {syncEnabled && (
            <button data-testid="prospects-sync-live-btn" onClick={syncToLive} disabled={syncBusy}
              title={`Push all new prospects to ${syncLiveUrl}`}
              className="inline-flex items-center gap-1.5 bg-indigo-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {syncBusy ? <Loader2 size={15} className="animate-spin" /> : <Cloud size={15} />}
              Sync new → Live
            </button>
          )}
          <button data-testid="prospects-refresh-delivery-btn" onClick={refreshDelivery} disabled={deliveryBusy}
            title="Query Brevo for delivery events over the last 14 days and update each prospect's delivery status (Delivered, Bounced, Blocked, Spam, …)"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-slate-50 disabled:opacity-50">
            {deliveryBusy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Refresh delivery
          </button>
          <button data-testid="prospects-send-all-btn" onClick={() => setBulkConfirm(true)} disabled={bulkBusy || !items.some((p) => p.status === "new")}
            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {bulkBusy ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
            Send to all new ({statusCounts.new || 0})
          </button>
        </div>
      </div>

      {syncResult && (
        <div data-testid="sync-result-banner" className={`rounded-xl border-2 p-4 ${syncResult.failed === 0 ? "bg-indigo-50 border-indigo-300" : "bg-amber-50 border-amber-300"}`}>
          <div className="flex items-start gap-3">
            {syncResult.failed === 0 ? <CheckCircle2 className="text-indigo-600 flex-shrink-0 mt-0.5" size={20} /> : <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">
                Live sync complete — {syncResult.pushed} pushed, {syncResult.skipped} already present{syncResult.failed ? `, ${syncResult.failed} failed` : ""}
              </p>
              {syncResult.errors?.length > 0 && (
                <ul className="text-xs text-amber-800 mt-1 space-y-0.5">
                  {syncResult.errors.slice(0, 5).map((er, i) => <li key={i}>• <strong>{er.company}</strong> — {er.error}</li>)}
                  {syncResult.errors.length > 5 && <li className="italic">…and {syncResult.errors.length - 5} more</li>}
                </ul>
              )}
              <p className="text-xs text-slate-500 mt-1">Live DB now has <strong>{syncResult.live_total}</strong> prospects.</p>
            </div>
            <button onClick={() => setSyncResult(null)} className="text-slate-400 hover:text-slate-600" aria-label="Dismiss"><X size={16} /></button>
          </div>
        </div>
      )}

      {job && (job.status === "running" || (job.status === "done" && Date.now() - new Date(job.finished_at || job.started_at).getTime() < 60000)) && (
        <div data-testid="bulk-progress-banner" className={`rounded-xl border-2 p-4 ${job.status === "running" ? "bg-blue-50 border-blue-300" : (job.failed === 0 ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300")}`}>
          <div className="flex items-center gap-3">
            {job.status === "running" ? <Loader2 className="animate-spin text-blue-600" size={22} /> : (job.failed === 0 ? <CheckCircle2 className="text-emerald-600" size={22} /> : <AlertTriangle className="text-amber-600" size={22} />)}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">
                {job.status === "running"
                  ? `Sending bulk outreach — ${job.sent} of ${job.total} delivered${job.failed ? `, ${job.failed} failed` : ""}`
                  : `Bulk send complete — ${job.sent} of ${job.total} delivered${job.failed ? `, ${job.failed} failed` : ""}`}
              </p>
              {job.status === "running" && job.current_company && (
                <p className="text-xs text-slate-600 truncate mt-0.5">Currently sending to <strong>{job.current_company}</strong> ({job.current_email})</p>
              )}
              {job.status === "done" && job.errors?.length > 0 && (
                <ul className="text-xs text-amber-800 mt-1 space-y-0.5">
                  {job.errors.slice(0, 5).map((er, i) => <li key={i}>• <strong>{er.company}</strong> — {er.error}</li>)}
                  {job.errors.length > 5 && <li className="italic">…and {job.errors.length - 5} more</li>}
                </ul>
              )}
            </div>
            <div className="text-right">
              <p className="font-mono text-xs text-slate-500">{Math.round(((job.progress || 0) / Math.max(1, job.total)) * 100)}%</p>
            </div>
          </div>
          <div className="mt-3 h-2 bg-white rounded-full overflow-hidden">
            <div className={`h-full transition-all duration-500 ${job.status === "running" ? "bg-blue-500" : (job.failed === 0 ? "bg-emerald-500" : "bg-amber-500")}`}
                 style={{ width: `${Math.min(100, ((job.progress || 0) / Math.max(1, job.total)) * 100)}%` }} />
          </div>
        </div>
      )}

      {deliveryResult && (
        <div data-testid="delivery-result-banner" className="rounded-xl border-2 p-4 bg-slate-50 border-slate-300">
          <div className="flex items-start gap-3">
            <RefreshCw className="text-slate-700 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">
                Brevo delivery sync — {deliveryResult.updated} prospect{deliveryResult.updated === 1 ? "" : "s"} updated from {deliveryResult.window_days}-day event window
              </p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                {Object.entries(deliveryResult.event_counts).filter(([, v]) => v > 0).map(([k, v]) => {
                  const meta = DELIVERY_META[k] || { label: k, color: "bg-slate-100 text-slate-600" };
                  return <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold ${meta.color}`}>{meta.label}: {v}</span>;
                })}
                {deliveryResult.unmatched_events > 0 && (
                  <span className="text-slate-500">· {deliveryResult.unmatched_events} Brevo events had no matching prospect (sent to members or outside this list)</span>
                )}
              </div>
            </div>
            <button onClick={() => setDeliveryResult(null)} className="text-slate-400 hover:text-slate-600" aria-label="Dismiss"><X size={16} /></button>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div data-testid="selection-toolbar" className="sticky top-0 z-20 rounded-xl border-2 border-emerald-400 bg-emerald-50 p-3 flex flex-wrap items-center gap-3 shadow-sm">
          <span className="font-bold text-sm text-emerald-900">
            {selected.size} selected
          </span>
          <button onClick={clearSelection} className="text-xs font-bold text-slate-600 hover:text-slate-900 underline">
            Clear
          </button>
          <div className="h-5 w-px bg-emerald-300" />
          <span className="text-xs text-emerald-900">Quick select:</span>
          <button onClick={() => selectByStatus("new")} className="text-xs font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded hover:bg-amber-200">+ All New</button>
          <button onClick={() => selectByStatus("sent")} className="text-xs font-bold text-blue-800 bg-blue-100 px-2 py-1 rounded hover:bg-blue-200">+ All Sent</button>
          <button onClick={() => selectByDelivery("hard_bounces")} className="text-xs font-bold text-rose-800 bg-rose-100 px-2 py-1 rounded hover:bg-rose-200">+ Hard bounces</button>
          <button onClick={() => selectByDelivery("soft_bounces")} className="text-xs font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded hover:bg-amber-200">+ Soft bounces</button>
          <button onClick={selectAllVisible} className="text-xs font-bold text-slate-700 bg-slate-200 px-2 py-1 rounded hover:bg-slate-300">+ All visible</button>
          <div className="flex-1" />
          <button
            data-testid="prospects-resend-selected-btn"
            onClick={() => setResendConfirm(true)}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white font-bold text-sm px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Resend to {selected.size} selected
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(STATUS_META).map(([k, meta]) => (
          <span key={k} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold ${meta.color}`}>
            <meta.icon size={11} /> {meta.label}: {statusCounts[k] || 0}
          </span>
        ))}
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold bg-slate-900 text-amber-300">
          Total: {items.length}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500">
          No prospects yet. Click <strong>Add prospect</strong> above to get started.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="prospects-list">
          {items.length > 0 && (
            <li className="flex items-center gap-3 px-3 py-1 text-xs text-slate-500">
              <button
                onClick={() => {
                  if (items.every(p => selected.has(p.id))) clearSelection();
                  else selectAllVisible();
                }}
                data-testid="prospects-select-all-toggle"
                className="inline-flex items-center gap-1.5 font-bold text-slate-600 hover:text-slate-900"
                title="Select / deselect all prospects on this page"
              >
                {items.every(p => selected.has(p.id)) ? <CheckSquare size={14} /> : <Square size={14} />}
                Select all ({items.length})
              </button>
            </li>
          )}
          {items.map((p) => {
            const Meta = STATUS_META[p.status] || STATUS_META.new;
            const isReady = p.email_to && p.email_subject && p.email_body;
            const Delivery = DELIVERY_META[p.delivery_status];
            const isSelected = selected.has(p.id);
            return (
              <li key={p.id} className={`bg-white border rounded-xl transition-colors duration-500 ${flashId === p.id ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-300" : isSelected ? "border-emerald-400 ring-1 ring-emerald-300" : "border-slate-200"}`} data-testid={`prospect-${p.id}`}>
                <div className="flex items-center gap-3 p-3 hover:bg-slate-50">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleOne(p.id); }}
                    data-testid={`prospect-check-${p.id}`}
                    aria-label={isSelected ? "Deselect" : "Select"}
                    className="flex-shrink-0 text-slate-400 hover:text-emerald-600"
                  >
                    {isSelected ? <CheckSquare size={18} className="text-emerald-600" /> : <Square size={18} />}
                  </button>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black uppercase ${Meta.color}`}>
                    <Meta.icon size={10} /> {Meta.label}
                  </span>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setOpen(open === p.id ? null : p.id)}>
                    <p className="font-bold text-sm truncate">{p.company_name}</p>
                    <p className="text-xs text-slate-500 truncate">{p.email_to} {p.hq && <>· {p.hq}</>} {p.employee_count && <>· {p.employee_count}</>}</p>
                  </div>
                  {!isReady && <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded flex items-center gap-1"><AlertCircle size={10} /> Not ready</span>}
                  {Delivery && (
                    <span data-testid={`prospect-delivery-${p.id}`} title={Delivery.hint + (p.delivery_reason ? `\nReason: ${p.delivery_reason}` : "")}
                          className={`text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 ${Delivery.color}`}>
                      <Delivery.icon size={10} /> {Delivery.label}
                    </span>
                  )}
                  {p.open_count > 0 && (
                    <span data-testid={`prospect-opens-${p.id}`} title={p.last_opened_at ? `Last opened ${new Date(p.last_opened_at).toLocaleString()}` : ""}
                          className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1">
                      <Eye size={10} /> {p.open_count}
                    </span>
                  )}
                  {p.click_count > 0 && (
                    <span data-testid={`prospect-clicks-${p.id}`} title={p.last_clicked_at ? `Last clicked ${new Date(p.last_clicked_at).toLocaleString()}` : ""}
                          className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded flex items-center gap-1">
                      <MousePointerClick size={10} /> {p.click_count}
                    </span>
                  )}
                  {p.sent_at && <span className="text-[10px] text-slate-400 hidden sm:block">sent {p.sent_at.slice(0, 10)}</span>}
                </div>
                {open === p.id && (
                  <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50/50" data-testid={`prospect-detail-${p.id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <p><span className="text-slate-500">Specialism:</span> {p.specialism || "—"}</p>
                      <p><span className="text-slate-500">Target roles:</span> {p.target_roles || "—"}</p>
                      <p><span className="text-slate-500">Phone:</span> {p.phone || "—"}</p>
                      <p><span className="text-slate-500">Website:</span> {p.website ? <a href={`https://${p.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="text-emerald-700 underline">{p.website}</a> : "—"}</p>
                    </div>
                    {(p.open_count > 0 || p.click_count > 0 || p.sent_at) && (
                      <div className="flex flex-wrap gap-3 text-xs bg-white border border-slate-200 rounded-lg p-3">
                        <span className="inline-flex items-center gap-1.5">
                          <Eye size={12} className="text-emerald-600" />
                          <strong>{p.open_count || 0}</strong> open{p.open_count === 1 ? "" : "s"}
                          {p.last_opened_at && <span className="text-slate-400">· last {new Date(p.last_opened_at).toLocaleString()}</span>}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MousePointerClick size={12} className="text-indigo-600" />
                          <strong>{p.click_count || 0}</strong> click{p.click_count === 1 ? "" : "s"}
                          {p.last_clicked_at && <span className="text-slate-400">· last {new Date(p.last_clicked_at).toLocaleString()}</span>}
                        </span>
                        {p.last_clicked_url && (
                          <span className="inline-flex items-center gap-1.5 min-w-0">
                            <span className="text-slate-500">Last URL:</span>
                            <span className="text-slate-700 truncate max-w-xs" title={p.last_clicked_url}>{p.last_clicked_url}</span>
                          </span>
                        )}
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Subject</p>
                      <p className="text-sm">{p.email_subject || <em className="text-amber-700">— missing —</em>}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Body preview</p>
                      <p className="text-sm whitespace-pre-wrap text-slate-700 line-clamp-6">{p.email_body || <em className="text-amber-700">— missing —</em>}</p>
                    </div>
                    {p.last_error && (
                      <div className="text-xs bg-red-50 border border-red-200 text-red-800 rounded p-2">
                        <strong>Last send error:</strong> {p.last_error}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <button data-testid={`prospect-edit-${p.id}`} onClick={() => setEditing(p)} className="text-xs font-bold bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-slate-400">Edit</button>
                      <button data-testid={`prospect-preview-${p.id}`} onClick={() => doPreview(p.id)} className="inline-flex items-center gap-1 text-xs font-bold bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-slate-400"><Eye size={11} /> Preview email</button>
                      <button data-testid={`prospect-send-${p.id}`} onClick={() => setSendConfirm(p)} disabled={!isReady || sendingId === p.id}
                        className="inline-flex items-center gap-1 text-xs font-bold bg-emerald-600 text-white rounded-lg px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-50">
                        {sendingId === p.id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                        Send email now
                      </button>
                      <div className="ml-auto flex gap-1">
                        {["new", "sent", "replied", "booked", "closed"].map((s) => (
                          <button key={s} data-testid={`prospect-status-${p.id}-${s}`}
                            onClick={async () => { await api.put(`/admin/prospects/${p.id}`, { status: s }); await load(); }}
                            disabled={p.status === s}
                            className={`text-[10px] font-bold px-2 py-1 rounded ${p.status === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 hover:border-slate-400"}`}>
                            {s}
                          </button>
                        ))}
                        <button onClick={() => remove(p.id)} className="text-xs text-red-500 hover:text-red-700 px-2"><Trash2 size={12} /></button>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto" data-testid="prospect-edit-modal">
          <div className="bg-white w-full max-w-3xl rounded-2xl my-8 relative">
            <button onClick={() => setEditing(null)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X size={20} /></button>
            <div className="p-6 border-b border-slate-100">
              <h2 className="font-display text-lg font-bold">{editing.id ? `Edit — ${editing.company_name}` : "Add prospect"}</h2>
            </div>
            <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">Company name *</span>
                  <input data-testid="prospect-field-company" value={editing.company_name} onChange={(e) => setEditing({ ...editing, company_name: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">Email to *</span>
                  <input data-testid="prospect-field-email" value={editing.email_to} onChange={(e) => setEditing({ ...editing, email_to: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">Contact first name</span>
                  <input data-testid="prospect-field-firstname" value={editing.contact_first_name} onChange={(e) => setEditing({ ...editing, contact_first_name: e.target.value })} placeholder='Used in salutation if your body uses "Hi [First Name]"' className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">Phone</span>
                  <input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">Website</span>
                  <input value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">HQ</span>
                  <input value={editing.hq} onChange={(e) => setEditing({ ...editing, hq: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">Employee count</span>
                  <input value={editing.employee_count} onChange={(e) => setEditing({ ...editing, employee_count: e.target.value })} placeholder="~600 employees" className={`${inputCls} mt-1`} /></label>
                <label className="block"><span className="text-xs font-bold uppercase text-slate-500">Target roles</span>
                  <input value={editing.target_roles} onChange={(e) => setEditing({ ...editing, target_roles: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block sm:col-span-2"><span className="text-xs font-bold uppercase text-slate-500">Specialism</span>
                  <input value={editing.specialism} onChange={(e) => setEditing({ ...editing, specialism: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block sm:col-span-2"><span className="text-xs font-bold uppercase text-slate-500">AI angle (internal notes)</span>
                  <textarea value={editing.ai_angle} onChange={(e) => setEditing({ ...editing, ai_angle: e.target.value })} rows={2} className={`${inputCls} mt-1`} /></label>
                <label className="block sm:col-span-2"><span className="text-xs font-bold uppercase text-slate-500">Email subject *</span>
                  <input data-testid="prospect-field-subject" value={editing.email_subject} onChange={(e) => setEditing({ ...editing, email_subject: e.target.value })} className={`${inputCls} mt-1`} /></label>
                <label className="block sm:col-span-2"><span className="text-xs font-bold uppercase text-slate-500">Email body * — plain text, blank line = paragraph break</span>
                  <textarea data-testid="prospect-field-body" value={editing.email_body} onChange={(e) => setEditing({ ...editing, email_body: e.target.value })} rows={12} className={`${inputCls} mt-1 font-mono text-xs`} /></label>
              </div>
            </div>
            <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="font-bold text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button data-testid="prospect-save-btn" onClick={save} disabled={busy} className="font-bold text-sm px-5 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                {busy ? "Saving…" : (editing.id ? "Save changes" : "Create prospect")}
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 overflow-y-auto" onClick={() => setPreview(null)} data-testid="prospect-preview-modal">
          <div className="bg-white w-full max-w-3xl rounded-2xl my-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreview(null)} className="absolute top-3 right-3 text-slate-500 hover:text-slate-900 z-10"><X size={20} /></button>
            <div className="p-5 border-b border-slate-100">
              <p className="text-xs text-slate-500">To: <strong>{preview.to}</strong></p>
              <p className="text-sm font-bold mt-1">Subject: {preview.subject}</p>
            </div>
            <div className="max-h-[70vh] overflow-y-auto bg-slate-100">
              <div dangerouslySetInnerHTML={{ __html: preview.html }} />
            </div>
          </div>
        </div>
      )}

      {sendConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="send-confirm-modal">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><Send size={18} /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-lg">Send real email now?</h3>
                <p className="text-sm text-slate-600 mt-1">A live Brevo email will be delivered to:</p>
                <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-bold text-sm">{sendConfirm.company_name}</p>
                  <p className="text-sm text-emerald-700">{sendConfirm.email_to}</p>
                  <p className="text-xs text-slate-500 mt-2 truncate"><strong>Subject:</strong> {sendConfirm.email_subject}</p>
                </div>
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">⚠ This cannot be undone. The prospect will receive the email immediately.</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setSendConfirm(null)} className="font-bold text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button data-testid="send-confirm-btn" onClick={() => sendOne(sendConfirm)} className="font-bold text-sm px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5">
                <Send size={14} /> Send email now
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkConfirm && (() => {
        const newList = items.filter((p) => p.status === "new" && p.email_to && p.email_subject && p.email_body);
        const alreadySent = items.filter((p) => p.status === "sent").length;
        const replied = items.filter((p) => p.status === "replied").length;
        const booked = items.filter((p) => p.status === "booked").length;
        const closed = items.filter((p) => p.status === "closed").length;
        const notReady = items.filter((p) => p.status === "new" && (!p.email_to || !p.email_subject || !p.email_body));
        const typedOk = bulkTypedConfirm.trim().toUpperCase() === "SEND";
        const estSecs = newList.length * 2;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto" data-testid="bulk-confirm-modal">
            <div className="bg-white w-full max-w-lg rounded-2xl p-6 my-8">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center"><AlertTriangle size={20} /></div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-lg">Send bulk outreach?</h3>
                  <p className="text-sm text-slate-600 mt-1">Review carefully before confirming. These are live cold emails.</p>
                </div>
              </div>

              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span>Total prospects on your list</span><strong>{items.length}</strong></div>
                <div className="flex justify-between text-emerald-700"><span>Will be sent now (status &quot;new&quot;)</span><strong>{newList.length}</strong></div>
                <div className="flex justify-between text-slate-500 text-xs pt-1 border-t border-slate-200"><span>Already sent</span><span>{alreadySent}</span></div>
                <div className="flex justify-between text-slate-500 text-xs"><span>Replied</span><span>{replied}</span></div>
                <div className="flex justify-between text-slate-500 text-xs"><span>Booked</span><span>{booked}</span></div>
                <div className="flex justify-between text-slate-500 text-xs"><span>Closed</span><span>{closed}</span></div>
                {notReady.length > 0 && (
                  <div className="flex justify-between text-amber-700 text-xs pt-1 border-t border-slate-200"><span>Missing to/subject/body — will be skipped</span><strong>{notReady.length}</strong></div>
                )}
                <div className="flex justify-between text-slate-500 text-xs pt-1 border-t border-slate-200"><span>Estimated run time</span><span>~{estSecs < 60 ? `${estSecs}s` : `${Math.ceil(estSecs/60)} min`}</span></div>
              </div>

              {newList.length > 0 ? (
                <>
                  <p className="text-xs font-bold text-slate-500 uppercase mt-4 mb-1">Recipients ({newList.length})</p>
                  <div className="max-h-40 overflow-y-auto bg-white border border-slate-200 rounded-lg text-xs divide-y divide-slate-100">
                    {newList.slice(0, 50).map((p) => (
                      <div key={p.id} className="px-3 py-1.5 flex justify-between gap-2">
                        <span className="font-bold truncate">{p.company_name}</span>
                        <span className="text-slate-500 truncate">{p.email_to}</span>
                      </div>
                    ))}
                    {newList.length > 50 && <div className="px-3 py-1.5 italic text-slate-500">…and {newList.length - 50} more</div>}
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-bold text-slate-700">
                      To confirm, type <code className="bg-slate-900 text-amber-300 px-1.5 py-0.5 rounded font-mono">SEND</code> below
                    </label>
                    <input
                      data-testid="bulk-confirm-type"
                      autoFocus
                      value={bulkTypedConfirm}
                      onChange={(e) => setBulkTypedConfirm(e.target.value)}
                      placeholder="Type SEND here"
                      className={`${inputCls} mt-1`}
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                  No prospects with status &quot;new&quot; and complete email fields. Nothing will be sent.
                </p>
              )}

              <div className="flex gap-2 justify-end mt-5">
                <button onClick={() => { setBulkConfirm(false); setBulkTypedConfirm(""); }} className="font-bold text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
                <button data-testid="bulk-confirm-btn" onClick={sendAll} disabled={newList.length === 0 || !typedOk}
                  className="font-bold text-sm px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                  <Zap size={14} /> Send {newList.length} email{newList.length === 1 ? "" : "s"} now
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {resendConfirm && (() => {
        const selectedList = items.filter((p) => selected.has(p.id));
        const ready = selectedList.filter((p) => p.email_to && p.email_subject && p.email_body);
        const notReady = selectedList.length - ready.length;
        const typedOk = resendTyped.trim().toUpperCase() === "RESEND";
        const estSecs = ready.length * 2;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto" data-testid="resend-confirm-modal">
            <div className="bg-white w-full max-w-lg rounded-2xl p-6 my-8">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><Send size={20} /></div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-lg">Resend to {selectedList.length} selected?</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    This will re-send the saved subject &amp; body to every ticked prospect — including those already marked &quot;Sent&quot;.
                    Use this for a nudge, template refresh, or after Brevo delivery was fixed.
                  </p>
                </div>
              </div>

              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span>Selected prospects</span><strong>{selectedList.length}</strong></div>
                <div className="flex justify-between text-emerald-700"><span>Will send</span><strong>{ready.length}</strong></div>
                {notReady > 0 && <div className="flex justify-between text-amber-700 text-xs"><span>Skipped (missing subject/body)</span><strong>{notReady}</strong></div>}
                <div className="flex justify-between text-slate-500 text-xs"><span>Estimated run time</span><span>~{estSecs < 60 ? `${estSecs}s` : `${Math.ceil(estSecs/60)} min`}</span></div>
              </div>

              {ready.length > 0 && (
                <>
                  <p className="text-xs font-bold text-slate-500 uppercase mt-4 mb-1">Recipients ({ready.length})</p>
                  <div className="max-h-40 overflow-y-auto bg-white border border-slate-200 rounded-lg text-xs divide-y divide-slate-100">
                    {ready.slice(0, 50).map((p) => (
                      <div key={p.id} className="px-3 py-1.5 flex justify-between gap-2">
                        <span className="font-bold truncate">{p.company_name}</span>
                        <span className="text-slate-500 truncate">{p.email_to}</span>
                      </div>
                    ))}
                    {ready.length > 50 && <div className="px-3 py-1.5 italic text-slate-500">…and {ready.length - 50} more</div>}
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-bold text-slate-700">
                      To confirm, type <code className="bg-slate-900 text-amber-300 px-1.5 py-0.5 rounded font-mono">RESEND</code> below
                    </label>
                    <input
                      data-testid="resend-confirm-type"
                      autoFocus
                      value={resendTyped}
                      onChange={(e) => setResendTyped(e.target.value)}
                      placeholder="Type RESEND here"
                      className={`${inputCls} mt-1`}
                    />
                  </div>
                </>
              )}

              <div className="flex gap-2 justify-end mt-5">
                <button onClick={() => { setResendConfirm(false); setResendTyped(""); }} className="font-bold text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
                <button data-testid="resend-confirm-btn" onClick={resendSelected} disabled={ready.length === 0 || !typedOk}
                  className="font-bold text-sm px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                  <Send size={14} /> Resend {ready.length} email{ready.length === 1 ? "" : "s"} now
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
